/**
 * 🦸‍♂️ DEKU - Mexam Affiliate Tracking Script
 * ===========================================
 * Plus Ultra affiliate tracking for Shopify theme!
 * Handles affiliate code capture and order attribution
 * 
 * @author Deku (Frontend Hero)
 * @date 2025-07-26
 * @quirk One For All (Affiliate Tracking)
 */

(function() {
  'use strict';

  const MexamAffiliate = {
    // Configuration - Plus Ultra settings!
    config: {
      cookieName: 'mexam_affiliate',
      cookieExpiry: 30, // days
      // Detección mejorada de URL para producción
      apiUrl: (function() {
        const hostname = window.location.hostname;
        // Producción: myshopify.com o mexam.com.mx
        if (hostname.includes('myshopify.com') || hostname.includes('mexam.com.mx')) {
          return 'https://app.mexam.com.mx/api';
        }
        // Desarrollo local
        return 'http://localhost:3000/api';
      })(),
      debugMode: false,
      notificationDuration: 5000,
      version: '2.0.0' // Dynamic display config from API
    },

    // Display configuration cache (fetched from API)
    displayConfig: null,

    // 🚀 Initialize tracking - Plus Ultra!
    init() {
      this.log('🚀 DEKU: Mexam Affiliate Tracker initialized - Plus Ultra!');
      this.captureAffiliate();
      this.bindCheckoutEvents();
      this.bindCartEvents();
      this.updateUI();
      this.setupPerformanceMonitoring();
    },

    // 📎 Capture affiliate code from URL - Hero style!
    async captureAffiliate() {
      const urlParams = new URLSearchParams(window.location.search);
      const affiliate = urlParams.get('aff') || urlParams.get('affiliate') || urlParams.get('ref');

      if (affiliate) {
        this.log('📎 DEKU: Affiliate code captured with hero precision:', affiliate);
        this.setAffiliateCookie(affiliate);
        this.trackClick(affiliate);
        this.cleanUrl();

        // Fetch display config and show notification
        await this.fetchDisplayConfig(affiliate);
        this.showSuccessNotification(affiliate);
      } else {
        // Check if we already have an affiliate in cookies
        const existingAffiliate = this.getAffiliateCookie();
        if (existingAffiliate) {
          this.log('🍪 DEKU: Existing affiliate found:', existingAffiliate);
          await this.fetchDisplayConfig(existingAffiliate);
          this.updateUI();
        }
      }
    },

    // 🎨 Fetch display configuration from API
    async fetchDisplayConfig(affiliateCode) {
      try {
        const response = await fetch(`${this.config.apiUrl}/affiliates/display-config/${affiliateCode}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.config) {
            this.displayConfig = data.config;
            this.log('🎨 DEKU: Display config fetched:', this.displayConfig);
            return this.displayConfig;
          }
        }

        this.log('⚠️ DEKU: Could not fetch display config, using defaults');
        return this.getDefaultDisplayConfig(affiliateCode);
      } catch (error) {
        this.log('❌ DEKU: Error fetching display config:', error);
        return this.getDefaultDisplayConfig(affiliateCode);
      }
    },

    // 🎨 Get default display configuration
    getDefaultDisplayConfig(affiliateCode) {
      return {
        affiliate_code: affiliateCode,
        affiliate_name: affiliateCode,
        display_mode: 'toast',
        display_style: 'standard',
        notification_text: `Comprando con ${affiliateCode}`,
        tagline: null,
        colors: {
          primary: '#4CAF50',
          text: '#FFFFFF'
        },
        auto_hide_seconds: 5,
        show_close_button: true
      };
    },

    // 🍪 Set affiliate cookie - One For All storage!
    setAffiliateCookie(code) {
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + this.config.cookieExpiry);
      
      const cookieValue = `${this.config.cookieName}=${code}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
      document.cookie = cookieValue;
      
      this.log('🍪 DEKU: Affiliate cookie set with hero strength:', code);
      
      // Store additional metadata
      try {
        localStorage.setItem('mexam_affiliate_meta', JSON.stringify({
          code: code,
          timestamp: Date.now(),
          referrer: document.referrer,
          userAgent: navigator.userAgent.substring(0, 100) // Truncated for privacy
        }));
      } catch (e) {
        this.log('⚠️ DEKU: Could not store affiliate metadata:', e);
      }
    },

    // 🔍 Get affiliate cookie - Detection quirk!
    getAffiliateCookie() {
      const name = this.config.cookieName + '=';
      const decodedCookie = decodeURIComponent(document.cookie);
      const cookieArray = decodedCookie.split(';');
      
      for (let cookie of cookieArray) {
        while (cookie.charAt(0) === ' ') {
          cookie = cookie.substring(1);
        }
        if (cookie.indexOf(name) === 0) {
          return cookie.substring(name.length, cookie.length);
        }
      }
      return null;
    },

    // 📊 Track affiliate click - Hero analytics!
    async trackClick(affiliateCode) {
      try {
        const trackingData = {
          referrer: document.referrer,
          url: window.location.href,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          screen: {
            width: screen.width,
            height: screen.height
          },
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight
          }
        };

        const response = await fetch(`${this.config.apiUrl}/affiliates/track/${affiliateCode}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(trackingData)
        });

        if (response.ok) {
          this.log('✅ DEKU: Click tracked successfully - Plus Ultra!');
          const result = await response.json();
          this.log('📈 DEKU: Tracking result:', result);
        } else {
          this.log('⚠️ DEKU: Click tracking failed with status:', response.status);
        }
      } catch (error) {
        this.log('❌ DEKU: Failed to track click:', error);
      }
    },

    // 🛒 Bind checkout events - Shopping hero mode!
    bindCheckoutEvents() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setupCheckoutListeners());
      } else {
        this.setupCheckoutListeners();
      }
    },

    // 🎯 Setup checkout listeners - Precision targeting!
    setupCheckoutListeners() {
      // Handle form submissions
      const checkoutForms = document.querySelectorAll('form[action*="checkout"], form[action*="cart/add"]');
      
      checkoutForms.forEach(form => {
        form.addEventListener('submit', (e) => {
          this.addAffiliateToCheckout(form);
        });
      });

      // Handle AJAX add to cart buttons
      document.addEventListener('click', (e) => {
        const target = e.target;
        
        // Check various selectors for add to cart buttons
        if (target.matches('[name="add"], .btn-checkout, .checkout-button, .add-to-cart, .product-form__cart-submit')) {
          const form = target.closest('form') || document.querySelector('form[action*="cart/add"]');
          if (form) {
            this.addAffiliateToCheckout(form);
          }
        }
      });

      // Handle Shopify AJAX cart events
      document.addEventListener('cart:item-added', () => {
        this.log('🛒 DEKU: Cart item added - checking affiliate attribution');
        this.ensureAffiliateInCart();
      });

      this.log('🎯 DEKU: Checkout listeners setup complete - Ready for action!');
    },

    // 🛍️ Bind cart events - Shopping cart hero!
    bindCartEvents() {
      // Listen for cart updates
      if (window.Shopify && window.Shopify.theme) {
        // Modern Shopify themes
        document.addEventListener('shopify:cart:updated', () => {
          this.ensureAffiliateInCart();
        });
      }

      // Legacy cart events
      document.addEventListener('ajaxCart:afterAddItem', () => {
        this.ensureAffiliateInCart();
      });
    },

    // 📝 Add affiliate to checkout - Hero attribution!
    addAffiliateToCheckout(form) {
      const affiliateCode = this.getAffiliateCookie();
      
      if (!affiliateCode || !form) {
        return;
      }

      // Remove existing affiliate fields to avoid duplicates
      const existingFields = form.querySelectorAll('input[name*="mexam_affiliate"], input[name*="attributes[mexam_affiliate]"]');
      existingFields.forEach(field => field.remove());

      // Add affiliate code as order attribute - Multiple strategies!
      const strategies = [
        'attributes[mexam_affiliate]',
        'properties[mexam_affiliate]',
        'note_attributes[mexam_affiliate]'
      ];

      strategies.forEach(attributeName => {
        const affiliateField = document.createElement('input');
        affiliateField.type = 'hidden';
        affiliateField.name = attributeName;
        affiliateField.value = affiliateCode;
        affiliateField.className = 'mexam-affiliate-field';
        
        form.appendChild(affiliateField);
      });

      // Also add to form data if it's being sent via fetch
      if (form.dataset.mexamAffiliate !== affiliateCode) {
        form.dataset.mexamAffiliate = affiliateCode;
      }
      
      this.log('🛒 DEKU: Affiliate code added to checkout with hero precision:', affiliateCode);
    },

    // 🔄 Ensure affiliate in cart - Persistent hero!
    ensureAffiliateInCart() {
      const affiliateCode = this.getAffiliateCookie();
      if (!affiliateCode) return;

      // Try to update cart notes with affiliate info
      if (window.fetch && window.Shopify) {
        this.updateCartWithAffiliate(affiliateCode);
      }
    },

    // 🛒 Update cart with affiliate - Cart hero mode!
    async updateCartWithAffiliate(affiliateCode) {
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        // Check if affiliate is already in cart attributes
        if (cart.attributes && cart.attributes.mexam_affiliate === affiliateCode) {
          return; // Already set
        }

        // Update cart with affiliate attribution
        const updateData = {
          attributes: {
            ...cart.attributes,
            mexam_affiliate: affiliateCode
          }
        };

        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData)
        });

        this.log('🛒 DEKU: Cart updated with affiliate attribution - Plus Ultra!');
      } catch (error) {
        this.log('⚠️ DEKU: Failed to update cart with affiliate:', error);
      }
    },

    // 🎨 Update UI elements - Visual hero mode!
    updateUI() {
      const affiliateCode = this.getAffiliateCookie();
      
      if (affiliateCode) {
        this.updateAffiliateLinks(affiliateCode);
        this.addAffiliateToExistingForms(affiliateCode);
      }
    },

    // 🎉 Show success notification - Victory celebration with dynamic config!
    showSuccessNotification(code) {
      const config = this.displayConfig || this.getDefaultDisplayConfig(code);

      // If display mode is 'none', don't show anything
      if (config.display_mode === 'none') {
        this.log('🔇 DEKU: Display mode is "none", skipping notification');
        return;
      }

      // Remove existing notifications
      const existingNotifications = document.querySelectorAll('.mexam-affiliate-notification');
      existingNotifications.forEach(notification => notification.remove());

      const notification = document.createElement('div');
      notification.className = `mexam-affiliate-notification mexam-display-${config.display_mode} mexam-style-${config.display_style}`;
      notification.style.setProperty('--mexam-primary-color', config.colors?.primary || '#4CAF50');
      notification.style.setProperty('--mexam-text-color', config.colors?.text || '#FFFFFF');

      // Build notification content based on style
      const affiliateName = config.affiliate_name || code;
      const notificationText = config.notification_text || `Comprando con ${affiliateName}`;
      const tagline = config.tagline;
      const showCloseButton = config.show_close_button !== false;

      let contentHTML = '';

      if (config.display_style === 'minimal') {
        contentHTML = `
          <div class="notification-content minimal">
            <span class="affiliate-text">${notificationText}</span>
            ${showCloseButton ? '<button class="notification-close" onclick="this.parentElement.parentElement.remove()" aria-label="Cerrar">×</button>' : ''}
          </div>
        `;
      } else if (config.display_style === 'prominent') {
        contentHTML = `
          <div class="notification-content prominent">
            <span class="affiliate-icon">🛍️</span>
            <div class="affiliate-details">
              <span class="affiliate-text">${notificationText}</span>
              ${tagline ? `<span class="affiliate-tagline">${tagline}</span>` : ''}
            </div>
            ${showCloseButton ? '<button class="notification-close" onclick="this.parentElement.parentElement.remove()" aria-label="Cerrar">×</button>' : ''}
          </div>
        `;
      } else {
        // Standard style
        contentHTML = `
          <div class="notification-content standard">
            <span class="affiliate-icon">🦸‍♂️</span>
            <span class="affiliate-text">${notificationText}</span>
            ${tagline ? `<span class="affiliate-tagline">${tagline}</span>` : ''}
            ${showCloseButton ? '<button class="notification-close" onclick="this.parentElement.parentElement.remove()" aria-label="Cerrar">×</button>' : ''}
          </div>
        `;
      }

      notification.innerHTML = contentHTML;

      // Add dynamic CSS styles
      this.addNotificationStyles();

      // Append to body based on display mode
      if (config.display_mode === 'header') {
        document.body.insertBefore(notification, document.body.firstChild);
      } else if (config.display_mode === 'footer') {
        document.body.appendChild(notification);
      } else if (config.display_mode === 'inline') {
        const targetContainer = document.querySelector('[data-mexam-affiliate-container]') ||
                                document.querySelector('.product-info') ||
                                document.querySelector('.product__info-container') ||
                                document.body;
        targetContainer.insertBefore(notification, targetContainer.firstChild);
      } else {
        // Toast (default)
        document.body.appendChild(notification);
      }

      // Auto-hide with animation (0 = no auto hide)
      const autoHideSeconds = config.auto_hide_seconds || 5;
      if (autoHideSeconds > 0) {
        setTimeout(() => {
          if (notification.parentElement) {
            notification.classList.add('fade-out');
            setTimeout(() => notification.remove(), 300);
          }
        }, autoHideSeconds * 1000);
      }

      this.log('🎉 DEKU: Success notification shown with dynamic config!', {
        mode: config.display_mode,
        style: config.display_style,
        autoHide: autoHideSeconds
      });
    },

    // 🎨 Add notification styles - Hero aesthetics with dynamic colors!
    addNotificationStyles() {
      if (document.getElementById('mexam-affiliate-styles')) return;

      const style = document.createElement('style');
      style.id = 'mexam-affiliate-styles';
      style.textContent = `
        /* Base notification styles with CSS variables */
        .mexam-affiliate-notification {
          --mexam-primary-color: #4CAF50;
          --mexam-text-color: #FFFFFF;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 14px;
          z-index: 9999;
          box-sizing: border-box;
        }

        /* ===== DISPLAY MODES ===== */

        /* Toast mode (default) - Fixed position top right */
        .mexam-display-toast {
          position: fixed;
          top: 20px;
          right: 20px;
          max-width: 360px;
          background: linear-gradient(135deg, var(--mexam-primary-color) 0%, color-mix(in srgb, var(--mexam-primary-color), black 15%) 100%);
          color: var(--mexam-text-color);
          padding: 16px 20px;
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
          animation: slideInFromRight 0.5s ease-out;
          border: 2px solid rgba(255, 255, 255, 0.2);
        }

        /* Header mode - Full width at top */
        .mexam-display-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: var(--mexam-primary-color);
          color: var(--mexam-text-color);
          padding: 12px 20px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          animation: slideInFromTop 0.4s ease-out;
          text-align: center;
        }

        /* Footer mode - Full width at bottom */
        .mexam-display-footer {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: var(--mexam-primary-color);
          color: var(--mexam-text-color);
          padding: 12px 20px;
          box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
          animation: slideInFromBottom 0.4s ease-out;
          text-align: center;
        }

        /* Inline mode - Embedded in page content */
        .mexam-display-inline {
          position: relative;
          background: linear-gradient(135deg, var(--mexam-primary-color) 0%, color-mix(in srgb, var(--mexam-primary-color), black 10%) 100%);
          color: var(--mexam-text-color);
          padding: 14px 18px;
          border-radius: 8px;
          margin: 10px 0;
          animation: fadeInScale 0.4s ease-out;
        }

        /* ===== DISPLAY STYLES ===== */

        /* Minimal style - Clean and simple */
        .mexam-style-minimal .notification-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .mexam-style-minimal .affiliate-text {
          font-weight: 400;
          font-size: 13px;
        }

        .mexam-style-minimal .notification-close {
          font-size: 16px;
          opacity: 0.7;
        }

        /* Standard style - Balanced appearance */
        .mexam-style-standard .notification-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .mexam-style-standard .affiliate-icon {
          font-size: 20px;
          animation: pulse 2s infinite;
        }

        .mexam-style-standard .affiliate-text {
          flex: 1;
          font-weight: 500;
        }

        .mexam-style-standard .affiliate-tagline {
          font-size: 11px;
          background: rgba(255, 255, 255, 0.2);
          padding: 2px 8px;
          border-radius: 10px;
          margin-left: 8px;
        }

        /* Prominent style - Eye-catching */
        .mexam-style-prominent .notification-content {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .mexam-style-prominent .affiliate-icon {
          font-size: 28px;
          animation: bounce 1s infinite;
        }

        .mexam-style-prominent .affiliate-details {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .mexam-style-prominent .affiliate-text {
          font-weight: 600;
          font-size: 16px;
        }

        .mexam-style-prominent .affiliate-tagline {
          font-size: 12px;
          opacity: 0.9;
          font-style: italic;
        }

        /* ===== ANIMATIONS ===== */

        @keyframes slideInFromRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        @keyframes slideInFromTop {
          from { transform: translateY(-100%); }
          to { transform: translateY(0); }
        }

        @keyframes slideInFromBottom {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }

        @keyframes fadeInScale {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }

        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }

        .mexam-affiliate-notification.fade-out {
          animation: fadeOut 0.3s ease-out forwards;
        }

        @keyframes fadeOut {
          to { opacity: 0; transform: translateY(-10px); }
        }

        /* ===== COMMON ELEMENTS ===== */

        .notification-close {
          background: none;
          border: none;
          color: var(--mexam-text-color);
          font-size: 20px;
          cursor: pointer;
          padding: 0;
          margin-left: 8px;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s, opacity 0.2s;
          opacity: 0.8;
        }

        .notification-close:hover {
          background: rgba(255, 255, 255, 0.2);
          opacity: 1;
        }

        /* ===== MOBILE RESPONSIVE ===== */
        @media (max-width: 768px) {
          .mexam-display-toast {
            top: 10px;
            right: 10px;
            left: 10px;
            max-width: none;
          }

          .mexam-affiliate-notification {
            font-size: 13px;
            padding: 12px 16px;
          }

          .mexam-style-prominent .affiliate-icon {
            font-size: 24px;
          }

          .mexam-style-prominent .affiliate-text {
            font-size: 14px;
          }
        }
      `;

      document.head.appendChild(style);
    },

    // 🔗 Update affiliate links - Link hero mode!
    updateAffiliateLinks(code) {
      const links = document.querySelectorAll('a[data-affiliate-link], a[href*="/products/"]');
      
      links.forEach(link => {
        try {
          const url = new URL(link.href, window.location.origin);
          url.searchParams.set('aff', code);
          link.href = url.toString();
          link.dataset.affiliateUpdated = 'true';
        } catch (e) {
          this.log('⚠️ DEKU: Could not update link:', link.href);
        }
      });

      this.log(`🔗 DEKU: Updated ${links.length} affiliate links - Hero networking!`);
    },

    // 📝 Add affiliate to existing forms - Form hero mode!
    addAffiliateToExistingForms(affiliateCode) {
      const forms = document.querySelectorAll('form[action*="cart/add"]:not([data-mexam-affiliate])');
      
      forms.forEach(form => {
        this.addAffiliateToCheckout(form);
        form.dataset.mexamAffiliate = affiliateCode;
      });
    },

    // 🧹 Clean URL parameters - URL hero cleanup!
    cleanUrl() {
      try {
        const url = new URL(window.location);
        const paramsToRemove = ['aff', 'affiliate', 'ref'];
        let hasChanges = false;

        paramsToRemove.forEach(param => {
          if (url.searchParams.has(param)) {
            url.searchParams.delete(param);
            hasChanges = true;
          }
        });

        if (hasChanges) {
          window.history.replaceState({}, document.title, url.toString());
          this.log('🧹 DEKU: URL cleaned with hero precision!');
        }
      } catch (error) {
        this.log('⚠️ DEKU: Could not clean URL:', error);
      }
    },

    // 📊 Setup performance monitoring - Analytics hero!
    setupPerformanceMonitoring() {
      // Track page performance
      if (window.performance && window.performance.timing) {
        window.addEventListener('load', () => {
          const perfData = {
            loadTime: window.performance.timing.loadEventEnd - window.performance.timing.navigationStart,
            domContentLoaded: window.performance.timing.domContentLoadedEventEnd - window.performance.timing.navigationStart,
            timestamp: Date.now()
          };

          this.log('📊 DEKU: Page performance data:', perfData);
          
          // Store performance data for affiliate analytics
          try {
            const affiliateCode = this.getAffiliateCookie();
            if (affiliateCode) {
              localStorage.setItem('mexam_affiliate_perf', JSON.stringify(perfData));
            }
          } catch (e) {
            // Storage not available
          }
        });
      }
    },

    // 🔍 Debug logging - Detective hero mode!
    log(...args) {
      if (this.config.debugMode || window.location.search.includes('debug=1') || window.location.search.includes('mexam_debug=1')) {
        console.log('[🦸‍♂️ DEKU Affiliate Tracker]', ...args);
      }
    },

    // 💪 Get version and stats - Hero status report!
    getStats() {
      const affiliateCode = this.getAffiliateCookie();
      return {
        version: this.config.version,
        hasAffiliate: !!affiliateCode,
        affiliateCode: affiliateCode,
        timestamp: Date.now(),
        pageUrl: window.location.href,
        referrer: document.referrer
      };
    },

    // 🧪 Test mode - Hero testing!
    testMode() {
      this.config.debugMode = true;
      this.log('🧪 DEKU: Test mode activated - Plus Ultra testing!');

      // Simulate affiliate capture
      const testCode = 'TEST_HERO_' + Math.random().toString(36).substr(2, 9).toUpperCase();
      this.setAffiliateCookie(testCode);
      this.showSuccessNotification(testCode);

      return {
        testCode,
        stats: this.getStats()
      };
    },

    // 🎯 Track checkout conversion - Victory hero mode!
    async trackConversion() {
      const affiliateCode = this.getAffiliateCookie();

      if (!affiliateCode) {
        this.log('⚠️ DEKU: No affiliate code found for conversion tracking');
        return;
      }

      this.log('🎯 DEKU: Checkout conversion detected - Plus Ultra tracking!');

      // Intentar obtener datos de Shopify checkout
      if (window.Shopify && window.Shopify.checkout) {
        const checkout = window.Shopify.checkout;

        const conversionData = {
          affiliate_code: affiliateCode,
          order_id: checkout.order_id || checkout.id,
          order_number: checkout.order_number,
          total_price: checkout.total_price,
          currency: checkout.currency,
          customer_email: checkout.email,
          customer_id: checkout.customer_id,
          line_items: (checkout.line_items || []).map(item => ({
            product_id: item.product_id,
            variant_id: item.variant_id,
            sku: item.sku,
            title: item.title,
            quantity: item.quantity,
            price: item.price
          })),
          source: 'checkout_tracker_v2',
          timestamp: new Date().toISOString()
        };

        this.log('📊 DEKU: Conversion data prepared:', conversionData);

        try {
          const response = await fetch(`${this.config.apiUrl}/affiliates/conversions`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Domain': window.location.hostname,
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify(conversionData)
          });

          if (response.ok) {
            const result = await response.json();
            this.log('✅ DEKU: Conversion tracked successfully - Victory Plus Ultra!', result);

            // Fire custom event for analytics
            if (window.dataLayer) {
              window.dataLayer.push({
                'event': 'mexam_affiliate_conversion',
                'affiliate_code': affiliateCode,
                'order_value': checkout.total_price,
                'order_id': checkout.order_id || checkout.id
              });
              this.log('📈 DEKU: Google Analytics event fired');
            }

            // Limpiar cookie después de conversión exitosa
            document.cookie = `${this.config.cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            this.log('🧹 DEKU: Affiliate cookie cleared after conversion');

            // Limpiar localStorage metadata
            try {
              localStorage.removeItem('mexam_affiliate_meta');
              localStorage.removeItem('mexam_affiliate_perf');
            } catch (e) {
              // Storage not available
            }
          } else {
            this.log('⚠️ DEKU: Conversion tracking failed with status:', response.status);
          }
        } catch (error) {
          this.log('❌ DEKU: Conversion tracking error:', error);
        }
      } else {
        this.log('⚠️ DEKU: Shopify checkout data not available');
      }
    }
  };

  // 🚀 Initialize when DOM is ready - Hero activation!
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => MexamAffiliate.init());
  } else {
    MexamAffiliate.init();
  }

  // 🌟 Expose for manual testing and debugging - Hero interface!
  window.MexamAffiliate = MexamAffiliate;
  window.DekuAffiliate = MexamAffiliate; // Hero alias!

  // 🎯 Detectar y ejecutar tracking de conversión en checkout success
  if (window.location.pathname.includes('/thank') ||
      window.location.pathname.includes('/orders/') ||
      window.location.search.includes('checkout_complete=1') ||
      document.body.classList.contains('template-customers-order')) {

    MexamAffiliate.log('🎉 DEKU: Order confirmation page detected - tracking conversion!');

    // Ejecutar tracking de conversión cuando esté listo
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => MexamAffiliate.trackConversion(), 1000);
      });
    } else {
      setTimeout(() => MexamAffiliate.trackConversion(), 1000);
    }
  }

  // 🎯 Hero message
  console.log('🦸‍♂️ DEKU: Mexam Affiliate Tracker v' + MexamAffiliate.config.version + ' loaded - Plus Ultra! Use MexamAffiliate.testMode() to test.');

})();